import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {ClickEventHandler} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import type {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';

/*
 * Props.
 */

export interface FilterButtonProps extends InteractionComponentProps {
  /** Sets the active state of the button. */
  isActive: boolean;

  isDisabled?: boolean;

  /** Handle clicks on the button part. */
  onClick?: ClickEventHandler;

  /** Forces the hover state. Useful for opening dropdowns. */
  hasForcedHover?: boolean;
  /** Whether to show a chevron. Useful for dropdown elements. */
  hasChevron?: boolean;
  /** Removes the border radius on the box. Defaults to false. */
  noBorderRadius?: boolean;

  color?: string;
}

interface StyledFilterButtonContentProps {
  $isActive: boolean;
  $isDisabled?: boolean;
  $hasIcon: boolean;
  $color?: string;
}

interface StyledFilterButtonWrapperDivProps {
  $hasForcedHover: boolean;
  $noBorderRadius: boolean;
  $isDisabled?: boolean;
}

interface StyledChevronIconProps {
  $color?: string;
  $isDisabled?: boolean;
}

/*
 * Constants.
 */

export const defaultFilterButtonBackgroundColor = {
  findBase: (theme: UserTheme) => theme.greys.shade20,
  findHover: (theme: UserTheme) => theme.greys.shade30,
};
export const activeFilterButtonBackgroundColor = {
  findBase: (theme: UserTheme) => theme.palette.blue.shade20,
  findHover: (theme: UserTheme) => theme.palette.blue.shade30,
};

/*
 * Styles.
 */

const StyledFilterButtonWrapperDiv = styled.div<StyledFilterButtonWrapperDivProps>`
  ${flexCenter};
  ${(p) => !p.$noBorderRadius && 'border-radius: 8px'};
  height: 30px;

  ${(p) => computeDisabledStylesForFilterButtonWrapperDiv(p, p.theme)}
`;

const StyledButton = styled(ChromelessButton)`
  ${flexCenter};
  height: 100%;

  &:focus {
    outline: none;
  }
`;

const StyledChevronIcon = styled(Icon)<StyledChevronIconProps>`
  margin-right: 7px;
  margin-left: 7px;

  ${(p) => computeStyledChevronIconStyles(p, p.theme)}
`;

export const StyledFilterButtonContent = styled.div<StyledFilterButtonContentProps>`
  ${ellipsis()};
  font-size: 14px;
  font-weight: ${fontWeights.medium};

  margin-left: 8px;

  ${(p) => computeFilterButtonContentStyles(p, p.theme)}
`;

/*
 * Component.
 */

export const FilterButton: FC<PropsWithChildren<FilterButtonProps>> = ({
  isActive,
  isDisabled,
  onClick,
  hasForcedHover,
  hasChevron = false,
  children,
  interactionId,
  noBorderRadius = false,
  color,
}) => {
  const renderContent = () => (
    <>
      <StyledFilterButtonContent
        $isActive={isActive}
        $isDisabled={isDisabled}
        $hasIcon={hasChevron}
        $color={color}
      >
        {children}
      </StyledFilterButtonContent>
      {hasChevron && <StyledChevronIcon name="chevronDownSmall" $color={color} $isDisabled={isDisabled} />}
    </>
  );

  // If we have a click handler, the interactionId will be on the button instead.
  const wrapperInteractionProps = onClick ? {} : buildInteractionIdProps(interactionId);

  return (
    <StyledFilterButtonWrapperDiv
      $hasForcedHover={Boolean(hasForcedHover)}
      $noBorderRadius={noBorderRadius}
      $isDisabled={isDisabled}
      aria-checked={isActive}
      {...wrapperInteractionProps}
    >
      {onClick ? (
        <StyledButton interactionId={interactionId} onClick={onClick} isDisabled={isDisabled}>
          {renderContent()}
        </StyledButton>
      ) : (
        renderContent()
      )}
    </StyledFilterButtonWrapperDiv>
  );
};

/*
 * Helpers.
 */

function computeDisabledStylesForFilterButtonWrapperDiv(
  p: StyledFilterButtonWrapperDivProps,
  theme: UserTheme,
) {
  let backgroundColor = p.$hasForcedHover
    ? defaultFilterButtonBackgroundColor.findHover(theme)
    : defaultFilterButtonBackgroundColor.findBase(theme);
  if (p.$isDisabled) {
    backgroundColor = 'none';
  }

  const hoverStyle = css`
    &:hover {
      background-color: ${defaultFilterButtonBackgroundColor.findHover(theme)};
    }

    &[aria-checked='true'] {
      background-color: ${p.$hasForcedHover
        ? activeFilterButtonBackgroundColor.findHover(theme)
        : activeFilterButtonBackgroundColor.findBase(theme)};

      &:hover {
        background-color: ${activeFilterButtonBackgroundColor.findHover(theme)};
      }
    }
  `;

  return css`
    background-color: ${backgroundColor};

    /* show border when disabled */
    ${p.$isDisabled &&
    css`
      border: 2px solid ${theme.greys.shade30};
    `}

    /* only change background color on hover if not disabled */
    ${!p.$isDisabled && hoverStyle}
  `;
}

function computeStyledChevronIconStyles(p: StyledChevronIconProps, theme: UserTheme) {
  let color = p.$color ?? theme.greys.shade70;
  if (p.$isDisabled) {
    color = theme.greys.shade60;
  }

  return css`
    color: ${color};
  `;
}

function computeFilterButtonContentStyles(p: StyledFilterButtonContentProps, theme: UserTheme) {
  let color = p.$color || (p.$isActive ? theme.greys.shade90 : theme.greys.shade80);
  if (p.$isDisabled) {
    color = theme.greys.shade50;
  }

  return css`
    color: ${color};
    margin-right: ${p.$hasIcon ? 1 : 10}px;
  `;
}
